import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogFooter,
    AlertDialogTrigger,
    AlertDialogOverlay,
    AlertDialogPortal,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useMutation, useQueryClient } from "react-query";
import { CapturePaymentFormValues, formSchema } from "pages/GenericOrderDetailsPage/types/payment";
import { useParams } from "react-router-dom";
import { convertFileToBase64 } from "pages/CreateRemit/documentation";
import { capturePayment } from "pages/GenericOrderDetailsPage/apis";
import { Spinner } from "components";
import { toast } from "sonner";

interface PaymentDialogProps {
    children?: React.ReactNode;
}

export function PaymentDialog({ children }: PaymentDialogProps) {
    const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);
    const [supportingDocFile, setSupportingDocFile] = useState<File | null>(null);
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();

    const orderId = useParams().id;

    // Initialize form with React Hook Form
    const form = useForm<CapturePaymentFormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            order_details_id: parseInt(orderId as string),
            ack_no: "",
            amount: undefined,
            Payment_Proof: undefined,
            Supporting_document: undefined,
        },
    });

    // Setup mutation with React Query
    const mutation = useMutation({
        mutationFn: capturePayment,
        onSuccess: () => {
            toast.success('Payment details submitted successfully');
            setOpen(false);
            form.reset();
            setPaymentProofFile(null);
            setSupportingDocFile(null);
            queryClient.invalidateQueries(["orderDetails"]);
        },
        onError: (error) => {
            toast.error('Error submitting payment details', {
                description: error instanceof Error ? error.message : "Please try again",
            });
        },
    });

    const onSubmit = (values: CapturePaymentFormValues) => {
        mutation.mutate(values)
    };

    const handlePaymentProofChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const base64 = await convertFileToBase64(file);
            setPaymentProofFile(file);
            form.setValue("Payment_Proof", base64, { shouldValidate: true });
        }
    };

    const handleSupportingDocChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const base64 = await convertFileToBase64(file);
            setSupportingDocFile(file);
            form.setValue("Supporting_document", base64, { shouldValidate: true });
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger asChild>
                {children}
            </AlertDialogTrigger>
            <AlertDialogPortal>
                {mutation.isLoading && <Spinner />}
                <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                <AlertDialogContent className="sm:max-w-md bg-white-A700 rounded-xl p-0">
                    <AlertDialogHeader className="flex items-start border-b px-4 py-5">
                        <AlertDialogTitle>Add payment details</AlertDialogTitle>
                    </AlertDialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="">
                            <div className="space-y-6 px-10 pb-5">
                                <FormField
                                    control={form.control}
                                    name="ack_no"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="required-field">Acknowledgement number</FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter acknowledgement number"
                                                    {...field}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="amount"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="required-field">Amount</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="number"
                                                    placeholder="Enter amount"
                                                    {...field}
                                                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="Payment_Proof"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="required-field">Payment proof</FormLabel>
                                            <div className="flex items-center justify-between border rounded-md  h-12 py-1.5 px-3.5">
                                                <div className="flex items-center gap-2">
                                                    <svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M3.078 21.529H13.3007C15.2935 21.529 16.2849 20.5183 16.2849 18.516V9.77561C16.2849 8.53361 16.1502 7.99475 15.3801 7.20536L10.0663 1.80529C9.33523 1.05409 8.73805 0.890625 7.65048 0.890625L3.078 0.890625C1.09507 0.890625 0.09375 1.91084 0.09375 3.91348L0.09375 18.516C0.09375 20.5277 1.09507 21.529 3.078 21.529ZM3.1548 19.9794C2.16334 19.9794 1.64338 19.4496 1.64338 18.4873L1.64338 3.94223C1.64338 2.98938 2.16334 2.44025 3.16466 2.44025H7.43855V8.03336C7.43855 9.2462 8.05463 9.84297 9.24816 9.84297H14.7353V18.4873C14.7353 19.4496 14.2248 19.9794 13.2239 19.9794H3.1548ZM9.42148 8.38904C9.04609 8.38904 8.89166 8.23543 8.89166 7.85018V2.73884L14.4363 8.38945L9.42148 8.38904Z" fill="#5E5E5E" />
                                                    </svg>

                                                    <span className="text-gray-500 truncate max-w-[150px] inline-block">
                                                        {paymentProofFile ? paymentProofFile.name : "Upload the documents"}
                                                    </span>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    className="text-blue-700 border border-blue-700"
                                                    onClick={() => {
                                                        document.getElementById('payment-proof-upload')?.click();
                                                    }}
                                                >
                                                    Upload
                                                </Button>
                                                <Input
                                                    id="payment-proof-upload"
                                                    type="file"
                                                    className="hidden"
                                                    onChange={handlePaymentProofChange}
                                                    ref={field.ref}
                                                    accept="image/*,application/pdf"
                                                />
                                            </div>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="Supporting_document"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="">Supporting document (optional)</FormLabel>
                                            <div className="flex items-center justify-between border rounded-md  h-12 py-1.5 px-3.5">
                                                <div className="flex items-center gap-2">
                                                    <svg width="17" height="22" viewBox="0 0 17 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M3.078 21.529H13.3007C15.2935 21.529 16.2849 20.5183 16.2849 18.516V9.77561C16.2849 8.53361 16.1502 7.99475 15.3801 7.20536L10.0663 1.80529C9.33523 1.05409 8.73805 0.890625 7.65048 0.890625L3.078 0.890625C1.09507 0.890625 0.09375 1.91084 0.09375 3.91348L0.09375 18.516C0.09375 20.5277 1.09507 21.529 3.078 21.529ZM3.1548 19.9794C2.16334 19.9794 1.64338 19.4496 1.64338 18.4873L1.64338 3.94223C1.64338 2.98938 2.16334 2.44025 3.16466 2.44025H7.43855V8.03336C7.43855 9.2462 8.05463 9.84297 9.24816 9.84297H14.7353V18.4873C14.7353 19.4496 14.2248 19.9794 13.2239 19.9794H3.1548ZM9.42148 8.38904C9.04609 8.38904 8.89166 8.23543 8.89166 7.85018V2.73884L14.4363 8.38945L9.42148 8.38904Z" fill="#5E5E5E" />
                                                    </svg>

                                                   <span className="text-gray-500 truncate max-w-[150px] inline-block">
                                                        {supportingDocFile ? supportingDocFile.name : "Upload the documents"}
                                                    </span>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    className="text-blue-700 border border-blue-700"
                                                    onClick={() => {
                                                        document.getElementById('supporting-doc-upload')?.click();
                                                    }}
                                                >
                                                    Upload
                                                </Button>
                                                <Input
                                                    id="supporting-doc-upload"
                                                    type="file"
                                                    className="hidden"
                                                    onChange={handleSupportingDocChange}
                                                    ref={field.ref}
                                                    accept="image/*,application/pdf"
                                                />
                                            </div>
                                            <p className="text-sm text-gray-500 mt-1">Eg: In case of name mismatch.</p>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <AlertDialogFooter className="flex justify-between rounded-b-xl p-4 bg-[#F9F9F9] flex-row gap-2 sm:justify-between">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="text-blue-700 border border-blue-700"
                                    onClick={() => {
                                        setOpen(false);
                                        form.reset();
                                        setPaymentProofFile(null);
                                        setSupportingDocFile(null);
                                    }}
                                >
                                    Close
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        !form.formState.isDirty ||
                                        !form.formState.isValid ||
                                        mutation.isLoading
                                    }

                                    className="bg-blue-600 hover:bg-blue-700 text-white-A700"
                                >
                                    {mutation.isLoading ? "Submitting..." : "Submit"}
                                </Button>
                            </AlertDialogFooter>
                        </form>
                    </Form>
                </AlertDialogContent>
            </AlertDialogPortal>
        </AlertDialog>
    );
}